import { saveBase64ImageWithEviction } from "../../features/shared/localStorageBase64"
import { OfficeTypeMap } from "../CMS/schema"
import { PracticeStatus } from "../Practice/schema"
import { LiabilityInsurance as PracticeLiabilityInsurance } from "../Practices/schema"
import {
  PracticeTypeDisplayId,
  ProviderTypeDisplayId,
} from "../enums/displayId.enum"
import {
  AvailableDisplayId,
  EmailTypeDisplayId,
  Provider,
  ProviderStatusDisplayId,
  ProviderTherapyTypeOptions,
  SpecialtyType,
} from "../Provider/schema"
import { LocationPhotoTypeEnum, PublicLocation, PublicPractice } from "./schema"

const SEARCH_PHOTO_KEY_PREFIX = "search-thumb-"

const searchResultPhotoFileId = (providerId: string) =>
  `${SEARCH_PHOTO_KEY_PREFIX}${providerId}`

// Base64-encoded magic-number prefixes for the browser-renderable formats the
// file-service normalizes thumbnails into. JPEG is the fallback.
const BASE64_MIME_PREFIXES: ReadonlyArray<readonly [string, string]> = [
  ["iVBORw0KGgo", "image/png"],
  ["R0lGODdh", "image/gif"],
  ["R0lGODlh", "image/gif"],
  ["UklGR", "image/webp"],
]

// The search index stores raw base64. Cached photos consumed by base64ToBlob
// need a data URL, so wrap before stashing in localStorage.
export const ensurePhotoDataUrl = (base64: string): string => {
  if (base64.startsWith("data:")) return base64

  const match = BASE64_MIME_PREFIXES.find(([prefix]) =>
    base64.startsWith(prefix),
  )
  return `data:${match?.[1] ?? "image/jpeg"};base64,${base64}`
}

const toPublicLocation = (loc: SearchLocation): PublicLocation => ({
  id: loc.LocationId,
  line1: loc.AddressLine1 ?? "",
  line2: loc.AddressLine2 ?? "",
  city: loc.City ?? "",
  stateOrTerritory: loc.State ?? loc.StateCode ?? "",
  postalCode: loc.PostalCode ?? "",
  fax: "",
  isPrimary: loc.IsPrimary ?? !!loc.IsPrimaryLocation,
  isAdaAccessible: loc.LocationAdaAccessible ?? false,
  officeRequirementsCertified: loc.OfficeRequirementsCertified ?? false,
  locationTypeDisplayId: loc.LocationType as keyof OfficeTypeMap,
  phones: (loc.Phones ?? []).map((p) => ({
    id: p.Id,
    number: p.Number,
    locationPhoneTypeDisplayId: p.LocationPhoneTypeDisplayId,
  })),
  emails: (loc.Emails ?? []).map((e) => ({
    id: e.Id,
    address: e.Address,
    locationEmailTypeDisplayId: e.LocationEmailTypeDisplayId,
  })),
  locationPhotos: (loc.LocationPhotos ?? []).map((photo) => ({
    id: photo.Id,
    name: photo.Name ?? undefined,
    fileName: photo.FileName,
    description: photo.Description ?? undefined,
    locationPhotoTypeDisplayId:
      photo.LocationPhotoTypeDisplayId as LocationPhotoTypeEnum,
    thumbnailBase64: photo.ThumbnailBase64 ?? undefined,
  })),
})

export const mapSearchResultToProviderProfileData = (
  result: ProcessedProviderSearchResults,
): { provider: Provider; publicPractices: PublicPractice[] } => {
  let photoFileId: string | undefined
  if (result.ThumbnailBase64) {
    photoFileId = searchResultPhotoFileId(result.ProviderId)
    saveBase64ImageWithEviction(
      photoFileId,
      ensurePhotoDataUrl(result.ThumbnailBase64),
    )
  }

  const providerLocations = (result.ProviderLocations ?? []).map((loc) => ({
    id: loc.LocationId,
    createdBy: "",
  }))

  const locationsByPractice = new Map<string, PublicLocation[]>()
  for (const loc of result.ProviderLocations ?? []) {
    const arr = locationsByPractice.get(loc.PracticeId) ?? []
    arr.push(toPublicLocation(loc))
    locationsByPractice.set(loc.PracticeId, arr)
  }

  const publicPractices: PublicPractice[] = (
    result.PracticeAssociations ?? []
  ).map((assoc) => ({
    id: assoc.PracticeId,
    name: assoc.PracticeName,
    status: assoc.PracticeStatus as PracticeStatus,
    isVisible: assoc.PracticeIsVisible,
    affiliateSince: "",
    locations: locationsByPractice.get(assoc.PracticeId) ?? [],
    liabilityInsurances: (assoc.PracticeLiabilityInsuranceStatuses ?? []).map(
      (insuranceStatus) =>
        ({ insuranceStatus }) as unknown as PracticeLiabilityInsurance,
    ),
    authReleaseRequired: assoc.AuthReleaseRequired ?? undefined,
    typeId: (assoc.PracticeType as PracticeTypeDisplayId) ?? undefined,
    billingAddress: null,
    contactInfo: null,
  }))

  const specialties = [
    ...(result.PrimarySpecialties ?? []).map((s) => ({
      specialtyDisplayId: s,
      specialtyTypeDisplayId: "primary" as SpecialtyType,
    })),
    ...(result.AdditionalSpecialties ?? []).map((s) => ({
      specialtyDisplayId: s,
      specialtyTypeDisplayId: "additional" as SpecialtyType,
    })),
  ]

  const provider: Provider = {
    id: result.ProviderId,
    userId: result.AccountId,
    isPublicViewable: result.ProviderIsPublicViewable,
    firstName: result.ProviderFirstName ?? undefined,
    lastName: result.ProviderLastName ?? undefined,
    biography: result.Biography ?? undefined,
    practicingSince: result.PracticingSince ?? undefined,
    gender: result.Gender ?? undefined,
    isTransgender: result.IsTransgender,
    hasLicense: result.ProviderHasLicense,
    isAcceptingNewPatients: result.IsAcceptingNewPatients,
    hasPatientWaitList: result.HasPatientWaitList,
    isAvailableEveningHours: result.IsAvailableEveningHours,
    isLanguageAttested: false,
    isVirtualAttested: result.IsVirtualAttested ?? undefined,
    isUnderSupervision: result.IsUnderSupervision,
    isInsurancePaneled: result.IsInsurancePaneled,
    photoFileId,
    providerPhoto: result.ThumbnailBase64
      ? {
          fileId: photoFileId ?? "",
          fileName: "",
          createdBy: "",
          createdByFullName: "",
          createdDateTime: "",
          fileStatusDisplayId: "available",
          thumbnailBase64: result.ThumbnailBase64,
        }
      : undefined,
    providerStatusDisplayId: result.ProviderStatus as ProviderStatusDisplayId,
    militaryStatusDisplayId: result.MilitaryStatus ?? undefined,
    faithOrientationDisplayId: result.FaithOrientation ?? undefined,
    providerTypeDisplayId:
      (result.ProviderType as ProviderTypeDisplayId) ??
      (result.IsManaged
        ? ProviderTypeDisplayId.MANAGED
        : ProviderTypeDisplayId.STANDARD),
    graduateDegreeDisplayId: result.GraduateDegreeDisplayId ?? undefined,
    graduateSchoolName: result.GraduateSchoolName ?? undefined,
    graduateYear: result.GraduateYear ?? undefined,
    providerAgesSeenDisplayIds: result.AgesSeen ?? [],
    providerAvailableDisplayIds: (result.Availables ??
      []) as unknown as AvailableDisplayId[],
    providerClientEthnicityDisplayIds: result.ClientEthnicities ?? [],
    providerCommunityDisplayIds: result.Communities ?? [],
    providerDesignationDisplayIds: result.Designations ?? [],
    providerEthnicGroupDisplayIds: result.EthnicGroups ?? [],
    providerInsuranceDisplayIds: result.InsuranceCarriers ?? [],
    providerLanguageDisplayIds: result.Languages ?? [],
    providerParticipantDisplayIds: result.Participants ?? [],
    providerReligionDisplayIds: result.Religion ?? [],
    providerSessionTypeDisplayIds: result.SessionTypes ?? [],
    providerSexualOrientationDisplayIds: result.SexualOrientations ?? [],
    providerTagDisplayIds: result.ProviderTags ?? [],
    providerTelehealthPlatforms: (result.TelehealthPlatforms ?? []).map(
      (p) => ({
        telehealthPlatformDisplayId: p as never,
      }),
    ),
    providerTherapyTypeDisplayIds: (result.TherapyTypes ??
      []) as ProviderTherapyTypeOptions[],
    providerLocations,
    liabilityInsurances: (result.LiabilityInsuranceStatus ?? []).map(
      (liabilityInsuranceStatusDisplayId) => ({
        liabilityInsuranceStatusDisplayId,
      }),
    ),
    providerBlockOutDate: (result.BlockOutDates ?? []).map((d) => ({
      startDate: d.StartDate,
      endDate: d.EndDate,
      reason: d.Reason,
    })),
    providerCredentials: (result.Credentials ?? []).map((c) => ({
      credentialTypeDisplayId: c.Type,
      stateCode: c.StateCode,
      expireDate: c.ExpireDate ? new Date(c.ExpireDate) : undefined,
    })),
    providerDisclosures: result.HasActiveDisclosure
      ? [{ fileId: "active", envelopeId: "active" }]
      : [],
    providerEmails: result.ProviderEmailDetails
      ? result.ProviderEmailDetails.map((e) => ({
          address: e.Address,
          emailTypeDisplayId: e.EmailTypeDisplayId as EmailTypeDisplayId,
        }))
      : (result.ProviderEmails ?? [])
          .filter((address): address is string => !!address)
          .map((address, i) => ({
            address,
            emailTypeDisplayId: (i === 0
              ? "primary"
              : "referral") as EmailTypeDisplayId,
          })),
    providerLicenses: (result.Licenses ?? []).map((l) => ({
      licenseTypeDisplayId: l.Type,
      licenseStatusDisplayId: l.LicenseStatus,
      stateCode: l.StateCode,
      licenseNumber: l.LicenseNumber,
      expireDate: l.ExpireDate ? new Date(l.ExpireDate) : undefined,
    })),
    providerPhones: result.ProviderPhoneDetails
      ? result.ProviderPhoneDetails.map((p) => ({
          number:
            p.CountryCode != null ? `${p.CountryCode}${p.Number}` : p.Number,
          phoneTypeDisplayId: p.PhoneTypeDisplayId,
        }))
      : (result.ProviderPhones ?? []).map((number) => ({ number })),
    providerSpecialties: specialties,
    providerAuthReleases: [],
    createdDateTime: "",
    createdByUserId: "",
    npiNumber: null,
  }

  return { provider, publicPractices }
}
